import { Info, Layers, Check } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Label } from "@/components/ui/label";
import { cn } from "@/utils/cn";

interface ParallelSelectorProps {
  value: number;
  onChange: (value: number) => void;
  /** Maximum number of parallel studies allowed (based on user tier) */
  maxParallel?: number;
  /** Whether the selector is disabled */
  disabled?: boolean;
  /** Optional className for the container */
  className?: string;
}

/**
 * ParallelSelector - Dropdown to select how many parallel study instances to launch
 *
 * Allows users to run multiple versions of the same study configuration,
 * each exploring the Bayesian optimization parameter space with different random seeds.
 */
export function ParallelSelector({
  value,
  onChange,
  maxParallel = 10,
  disabled = false,
  className,
}: ParallelSelectorProps) {
  // Generate options from 1 to maxParallel
  const options = Array.from({ length: maxParallel }, (_, i) => i + 1);

  return (
    <div className={cn("space-y-2.5", className)}>
      <div className="flex items-center gap-2">
        <Label htmlFor="parallel-count" className="text-sm font-medium">
          Parallel Versions
        </Label>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button type="button" className="inline-flex items-center">
                <Info className="w-4 h-4 text-muted-foreground hover:text-brand-purple transition-colors" />
              </button>
            </TooltipTrigger>
            <TooltipContent className="bg-dark-800 text-white p-3 rounded-lg max-w-xs border border-dark-700 shadow-2xl">
              <p className="text-sm leading-relaxed">
                Launch multiple parallel study instances to explore different
                regions of the parameter space simultaneously. Each instance
                starts with a different random seed, potentially discovering
                different optimal configurations.
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      <Select
        value={value.toString()}
        onValueChange={(v) => onChange(parseInt(v, 10))}
        disabled={disabled}
      >
        <SelectTrigger id="parallel-count" className="w-full">
          <SelectValue>
            <div className="flex items-center gap-2.5">
              <div className="p-1.5 rounded bg-brand-purple/10">
                <Layers className={cn(
                  "w-4 h-4",
                  value === 1 ? "text-muted-foreground" : "text-brand-purple"
                )} />
              </div>
              <span className="font-medium text-sm">
                {value === 1 ? "1 version" : `${value} versions`}
              </span>
            </div>
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {options.map((count) => (
            <SelectItem key={count} value={count.toString()}>
              <div className="flex items-center gap-2.5 w-full">
                <div className="p-1.5 rounded bg-brand-purple/10">
                  <Layers className={cn(
                    "w-3.5 h-3.5",
                    count === 1 ? "text-muted-foreground" : "text-brand-purple"
                  )} />
                </div>
                <div className="text-left flex-1">
                  <div className="font-medium text-sm">
                    {count === 1 ? "1 version" : `${count} versions`}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {count === 1
                      ? "Single optimization run"
                      : `${count} parallel exploration seeds`}
                  </div>
                </div>
                {count === value && (
                  <Check className="w-4 h-4 text-brand-purple ml-auto" />
                )}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export default ParallelSelector;
